import React from 'react';
import { Settings, Loader2, RotateCcw } from 'lucide-react';
import { cn } from '@/utils';
import { AISettingsData, AI_SETTINGS_RANGES } from '@/utils/aiSettingsStorage';

interface AISettingsProps {
  settings: AISettingsData;
  onSettingsChange: (settings: AISettingsData) => void;
  onResetSettings?: () => void;
  filesCount: number;
  onUpload: () => void;
  isLoading?: boolean;
  showCreditModal?: boolean;
  className?: string;
}

const AISettings: React.FC<AISettingsProps> = ({
  settings,
  onSettingsChange,
  onResetSettings,
  filesCount,
  onUpload,
  isLoading = false,
  showCreditModal = false,
  className
}) => {
  const updateSetting = (key: keyof AISettingsData, value: number | string) => {
    onSettingsChange({
      ...settings,
      [key]: value
    });
  };

  return (
    <div className={cn('bg-white rounded-2xl border border-gray-200 p-6', className)}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Settings className="w-5 h-5 text-purple-600" />
          <h2 className="text-xl font-bold text-foreground">AI Settings</h2>
        </div>
        {onResetSettings && (
           <button
             onClick={onResetSettings}
             className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors duration-200 group cursor-pointer"
             title="Reset to default settings"
           >
             <RotateCcw className="w-4 h-4 group-hover:rotate-180 transition-transform duration-300" />
           </button>
         )}
      </div>

      <div className="space-y-6">
        {/* Max Title Length */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="text-sm font-medium text-foreground">
              Max Title Length
            </label>
            <span className="text-sm text-muted-foreground">
              {settings.title_length} chars
            </span>
          </div>
          <input 
            type="range" 
            min={AI_SETTINGS_RANGES.title_length.min}
            max={AI_SETTINGS_RANGES.title_length.max}
            value={settings.title_length}
            onChange={(e) => updateSetting('title_length', parseInt(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider" 
          />
        </div>

        {/* Max Description Length */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="text-sm font-medium text-foreground">
              Max Description Length
            </label>
            <span className="text-sm text-muted-foreground">
              {settings.description_length} chars
            </span>
          </div>
          <input 
            type="range" 
            min={AI_SETTINGS_RANGES.description_length.min}
            max={AI_SETTINGS_RANGES.description_length.max}
            value={settings.description_length}
            onChange={(e) => updateSetting('description_length', parseInt(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider" 
          />
        </div>

        {/* Max Keywords */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="text-sm font-medium text-foreground">
              Max Keywords
            </label>
            <span className="text-sm text-muted-foreground">
              {settings.keywords_count} keywords
            </span>
          </div>
          <input 
            type="range" 
            min={AI_SETTINGS_RANGES.keywords_count.min}
            max={AI_SETTINGS_RANGES.keywords_count.max}
            value={settings.keywords_count}
            onChange={(e) => updateSetting('keywords_count', parseInt(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider" 
          />
        </div>

        {/* Custom Instruction */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-foreground">
              Custom Instruction
            </label>
            <span className="text-sm text-muted-foreground">
              {settings.custom_instruction.length}/150
            </span>
          </div>
          <textarea
            value={settings.custom_instruction}
            onChange={(e) => {
              if (e.target.value.length <= 150) {
                updateSetting('custom_instruction', e.target.value);
              }
            }}
            placeholder="AI will try its best to understand your instructions, but it's recommended to leave empty for best results..."
            className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none transition-all duration-200 ${
              settings.custom_instruction.length >= 150 
                ? 'border-red-300 bg-red-50' 
                : 'border-gray-300'
            }`}
            rows={3}
            maxLength={150}
          />
          {settings.custom_instruction.length >= 150 && (
            <p className="text-xs text-red-500 mt-1">
              Maximum 150 characters reached
            </p>
          )}
        </div>
      </div>

      {/* Upload Button */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <button
          onClick={onUpload}
          disabled={filesCount === 0 || isLoading || showCreditModal}
          className={cn(
            'w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-300 shadow-lg',
            'flex items-center justify-center space-x-2',
            filesCount === 0 || isLoading || showCreditModal
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700 hover:shadow-xl transform hover:scale-[1.02]'
          )}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Validating Credits...</span>
            </>
          ) : (
            <>
              <span>Generate Metadata</span>
            </>
          )}
        </button>
        
        {/* Terms of Service */}
        {filesCount > 0 && (
          <p className="text-xs text-gray-600 text-center mt-4">
            By uploading you agree to our{' '}
            <a 
              href="/terms-of-service" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-blue-600 hover:underline"
            >
              terms of service
            </a>
          </p>
        )}
      </div>
    </div>
  );
};

export default AISettings;